import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Un message reste affiché 20 secondes, ou jusqu'à ce que l'utilisateur le ferme. */
const TOAST_DURATION_MS = 20_000;

type ToastTone = "success" | "info" | "error";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  /** En train de sortir (animation de 160 ms avant de disparaître). */
  leaving?: boolean;
}

const EXIT_MS = 160;

interface ToastContextValue {
  notify: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue>({ notify: () => {} });

// Hook du contexte, volontairement à côté de son fournisseur (seul le rechargement à chaud en pâtit).
// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

const toneStyles: Record<ToastTone, { border: string; icon: string; iconColor: string }> = {
  success: { border: "border-primary-200", icon: "ri-checkbox-circle-fill", iconColor: "text-primary-600" },
  info: { border: "border-secondary-200", icon: "ri-information-fill", iconColor: "text-secondary-600" },
  error: { border: "border-accent-200", icon: "ri-error-warning-fill", iconColor: "text-accent-600" },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  /** Un message sort en glissant vers le bas (même chemin qu'à l'entrée), puis disparaît. */
  const leave = useCallback((id: number) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), EXIT_MS);
  }, []);

  const notify = useCallback((message: string, tone: ToastTone = "success") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => leave(id), TOAST_DURATION_MS);
  }, [leave]);

  const dismiss = (id: number) => leave(id);

  return (
    <ToastContext.Provider value={{ notify }}>
      {children}
      {/* Au-dessus de tout, fenêtres comprises (les fenêtres sont aussi rendues dans <body>). */}
      {createPortal(
      <div
        className="fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[300] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-[min(380px,calc(100vw-2rem))]"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => {
          const style = toneStyles[toast.tone];
          return (
            <div
              key={toast.id}
              role={toast.tone === "error" ? "alert" : undefined}
              className={`${toast.leaving ? "animate-toast-out" : "animate-toast-in"} flex items-start gap-3 rounded-lg border bg-background-50 px-4 py-3 shadow-pop ${style.border}`}
            >
              <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${style.icon} text-lg ${style.iconColor}`}></i>
              </span>
              <p className="min-w-0 flex-1 text-sm text-foreground-800 leading-snug break-words">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="press -mr-1 -mt-1 w-9 h-9 sm:w-7 sm:h-7 shrink-0 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-800 cursor-pointer"
                aria-label="Fermer le message"
                title="Fermer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          );
        })}
      </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}